// File: packages/core/src/components/chat/ChatContainer.jsx
import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    Alert,
    Button,
    IconButton,
    Stack,
} from '@mui/material';
import { styled, alpha, useTheme } from '@mui/material/styles';
import ChatMessageList from './ChatMessageList';
import ChatHeader from './ChatHeader';
import ChatInput from './ChatInput';
import { useMessageContext } from '../../context/MessageContext';
import { useSession } from '../../context/SessionContext';
import { useTts } from '../../context/TtsContext';
import { useUI } from '../../context/UIContext';
import { useChatSessions } from '../../context/ChatSessionContext';
import { useAuth } from '../../context/AuthContext';
import { useConfig } from '../../context/ConfigContext';
import { useChatActions } from '../../hooks/useChatActions';
import { useAlert } from '../../context/AlertContext';
import ConditionalTooltip from '../common/ConditionalTooltip';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import AddCommentIcon from '@mui/icons-material/AddComment';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AlertWidget from '../widgets/AlertWidget';

const ChatArea = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'hasContentAbove',
})(({ theme, hasContentAbove }) => ({
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 0',
    minHeight: 0,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    overflow: 'hidden',
    overscrollBehaviorY: 'contain',
    position: 'relative',
}));

const MessagesList = styled(Box)(({ theme }) => ({
    flexGrow: 1,
    overflow: 'hidden',
    width: '100%',
    minHeight: 0,
    position: 'relative',
}));

const GutterWrapper = styled(Box)(({ theme }) => ({
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    width: '100%',
    [theme.breakpoints.down('md')]: {
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
}));

const ChatContainer = () => {
    const {
        sessionId,
        isConnected,
        isInitializing,
        timelineItems = [],
        isSending,
        hasAuthFailed,
        showProcessUpdates,
        sendMessage,
        resetState,
        preChatState,
        initializeSession,
        teamName,
        activeWidgets,
        toggleWidget,
    } = useSession();

    const {
        isWidgetVisible,
        toggleAudioMode,
        isAutoPlayEnabled
    } = useTts();

    const { isSubscriptionRequired } = useAuth();
    const { isMobile, openResources, isContentMinimized } = useUI();
    const { chatSessions, updateSessionMetadata, loadChatSessions: reloadChatSessionList } = useChatSessions();
    const { preChatFormComponent: PreChatFormComponent, chatTeamConfigs, widgets } = useConfig();
    const { getFormattedMessage, getActiveContextDescription, hasActiveContext } = useMessageContext();
    const { alert } = useAlert();
    const theme = useTheme();

    const {
        error,
        setError,
        currentSessionDisplayInfo,
        onNewSessionRequest,
        onSaveSessionRequest,
        renderDialogs,
    } = useChatActions({
        sessionId,
        chatSessions,
        updateSessionMetadata,
        resetState,
        reloadChatSessionList,
    });

    // LOW PRIORITY PRE-FETCH
    // We want the heavy Markdown/Katex bundle to load *after* the app is interactive
    // and the chat session is initialized, but *before* the user receives a large response.
    // requestIdleCallback ensures this runs only when the main thread/network is idle.
    useEffect(() => {
        const preloadMarkdown = () => {
            import('./message/LazyMarkdown').catch(() => {
                // Ignore errors here; if it fails now, it will retry when actually rendered
            });
        };

        if ('requestIdleCallback' in window) {
            // Wait for idle time, but force load after 5 seconds if idle never happens
            const handle = window.requestIdleCallback(preloadMarkdown, { timeout: 5000 });
            return () => window.cancelIdleCallback(handle);
        } else {
            // Fallback for browsers without requestIdleCallback
            const timer = setTimeout(preloadMarkdown, 2000);
            return () => clearTimeout(timer);
        }
    }, []);

    const hasContentAbove = openResources.length > 0;
    const isSessionReady = isConnected && !hasAuthFailed && !isInitializing && !isSubscriptionRequired;

    const headerButtons = useMemo(() => {
        const teamConfig = chatTeamConfigs?.[teamName];
        const buttonConfig = teamConfig?.headerButtons;
        const allowedWidgetIds = teamConfig?.widgets || [];

        const widgetButtons = allowedWidgetIds.map(widgetId => {
            const widgetDef = widgets?.[widgetId];
            if (!widgetDef) return null;
            if (widgetId === 'audioController') return null; // AudioController is handled separately
            const isActive = activeWidgets.includes(widgetId);

            return (
                 <ConditionalTooltip key={widgetId} title={isActive ? `Hide ${widgetDef.title}` : `Show ${widgetDef.title}`}>
                    <IconButton
                        onClick={() => toggleWidget(widgetId)}
                        size="small"
                        sx={{
                            border: 1,
                            borderColor: 'divider',
                            color: isActive ? 'primary.main' : 'default',
                            bgcolor: isActive ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                            padding: 0.5,
                            '&:hover': {
                                bgcolor: isActive ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.action.hover, 0.1)
                            }
                        }}
                    >
                        {widgetDef.icon}
                    </IconButton>
                </ConditionalTooltip>
            );
        });

        if (alert) {
            const isAlertWidgetActive = activeWidgets.includes('alertWidget');
            const severityColor = alert.severity === 'warning' ? 'warning' : 'primary'; // Use primary for info/success, warning for warning, error for error
            widgetButtons.unshift(
                <ConditionalTooltip key="alert-toggle" title={isAlertWidgetActive ? "Hide Alert" : "Show Alert"}>
                    <IconButton
                        onClick={() => toggleWidget('alertWidget')}
                        size="small"
                        sx={{
                            border: 1,
                            borderColor: isAlertWidgetActive ? `${severityColor}.main` : 'divider',
                            color: isAlertWidgetActive ? `${severityColor}.main` : 'text.secondary',
                            bgcolor: isAlertWidgetActive
                                ? alpha(theme.palette[severityColor].main, 0.1)
                                : 'transparent',
                            padding: 0.5,
                            animation: !isAlertWidgetActive ? 'pulse 2s infinite' : 'none',
                            '@keyframes pulse': {
                                '0%': { transform: 'scale(1)' },
                                '50%': { transform: 'scale(1.1)' },
                                '100%': { transform: 'scale(1)' },
                            },
                            '&:hover': {
                                bgcolor: isAlertWidgetActive
                                    ? alpha(theme.palette[severityColor].main, 0.2)
                                    : alpha(theme.palette.action.hover, 0.1)
                            }
                        }}
                    >
                        <NotificationsActiveIcon fontSize="small" />
                    </IconButton>
                </ConditionalTooltip>
            );
        }

        const defaultButtons = {
            audio: (
                <ConditionalTooltip key="audio" title={isWidgetVisible ? "Turn Off Audio Player" : "Turn On Audio Player (Auto-Play)"}>
                    <IconButton
                        onClick={toggleAudioMode}
                        size="small"
                        sx={{
                            border: 1,
                            borderColor: isWidgetVisible ? alpha(theme.palette.primary.main, 0.5) : 'divider',
                            color: isWidgetVisible ? 'primary.main' : 'text.secondary',
                            bgcolor: isWidgetVisible ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                            padding: 0.5,
                            '&:hover': {
                                bgcolor: isWidgetVisible ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.action.hover, 0.1)
                            }
                        }}
                    >
                        {isWidgetVisible ? <GraphicEqIcon fontSize="small" /> : <VolumeOffIcon fontSize="small" />}
                    </IconButton>
                </ConditionalTooltip>
            ),
            save: isMobile ? (
                <ConditionalTooltip key="save" title={currentSessionDisplayInfo?.save ? "Edit Session Name" : "Save Current Session"}>
                    <span>
                        <IconButton onClick={onSaveSessionRequest} disabled={!isSessionReady} size="small" sx={{ border: 1, borderColor: 'divider', padding: 0.5 }}>
                            {currentSessionDisplayInfo?.save ? <EditIcon fontSize="small" /> : <SaveIcon fontSize="small" />}
                        </IconButton>
                    </span>
                </ConditionalTooltip>
            ) : (
                <ConditionalTooltip key="save" title={currentSessionDisplayInfo?.save ? "Edit Session Name" : "Save Current Session"}>
                    <span>
                        <Button variant="outlined" size="small" startIcon={currentSessionDisplayInfo?.save ? <EditIcon /> : <SaveIcon />} onClick={onSaveSessionRequest} disabled={!isSessionReady} sx={{ py: 0.25 }}>
                            {currentSessionDisplayInfo?.save ? "Edit Name" : "Save Session"}
                        </Button>
                    </span>
                </ConditionalTooltip>
            ),
            new: isMobile ? (
                <ConditionalTooltip key="new" title="Start a New Session">
                    <span>
                        <IconButton onClick={onNewSessionRequest} disabled={!isSessionReady} size="small" sx={{ border: 1, borderColor: 'divider', padding: 0.5 }}>
                            <AddCommentIcon fontSize="small" />
                        </IconButton>
                    </span>
                </ConditionalTooltip>
            ) : (
                <ConditionalTooltip key="new" title="Start a New Session">
                    <span>
                        <Button variant="outlined" size="small" startIcon={<AddCommentIcon />} onClick={onNewSessionRequest} disabled={!isSessionReady} sx={{ py: 0.25 }}>
                            New Session
                        </Button>
                    </span>
                </ConditionalTooltip>
            ),
        };

        const buttonsToRender = [...widgetButtons.filter(Boolean)];

        // Apply button configurations from teamConfig or use defaults
        if (!buttonConfig) {
            buttonsToRender.push(defaultButtons.audio, defaultButtons.save, defaultButtons.new);
        } else {
            if (buttonConfig.audio !== false) buttonsToRender.push(defaultButtons.audio);
            if (buttonConfig.save !== false) buttonsToRender.push(defaultButtons.save);
            if (buttonConfig.new !== false) buttonsToRender.push(defaultButtons.new);
            if (buttonConfig.custom && Array.isArray(buttonConfig.custom)) {
                buttonConfig.custom.forEach(customBtn => {
                    buttonsToRender.push(React.cloneElement(customBtn.component, { key: customBtn.id }));
                });
            }
        }

        return buttonsToRender;
    }, [teamName, chatTeamConfigs, isWidgetVisible, toggleAudioMode, currentSessionDisplayInfo, onSaveSessionRequest, onNewSessionRequest, isSessionReady, isMobile, widgets, activeWidgets, toggleWidget, theme, alert]);

    const handleSendMessage = useCallback(async (content) => {
        if (!content || !sendMessage) return false;
        const messageToSend = getFormattedMessage(content);
        const success = await sendMessage(messageToSend);
        if (!success) {
            setError("Failed to send message. Please try again.");
        }
        return success;
    }, [sendMessage, getFormattedMessage, setError]);

    const getPlaceholderText = useCallback(() => {
        const hasContextValue = typeof hasActiveContext === 'function' ? hasActiveContext() : hasActiveContext;
        if (!hasContextValue) return "Type your message here...";

        const descriptions = getActiveContextDescription();
        if (!descriptions || descriptions.length === 0) return "Type your message here...";

        const questionContext = descriptions.find(desc => desc.type === 'question');
        if (questionContext) return `Ask about ${questionContext.text}...`;

        return "Type your message with context...";
    }, [hasActiveContext, getActiveContextDescription]);

    const renderedWidgets = useMemo(() => {
        const widgetsToShow = [];
        if (activeWidgets.includes('alertWidget') && alert) {
            widgetsToShow.push(<AlertWidget key="alertWidget" />);
        }

        if (widgets) {
             activeWidgets.forEach(widgetId => {
                 if (widgetId === 'audioController') return; // AudioController is handled separately
                 if (widgetId === 'alertWidget') return; // AlertWidget is handled separately
                 const widgetDef = widgets[widgetId];
                 if (widgetDef && widgetDef.component) {
                     const WidgetComponent = widgetDef.component;
                     widgetsToShow.push(<WidgetComponent key={widgetId} />);
                 }
             });
        }

        // Always include AudioController if visible, regardless of team config
        if (isWidgetVisible && widgets && widgets['audioController']) {
             const AudioWidget = widgets['audioController'].component;
             widgetsToShow.push(<AudioWidget key="audioController" />);
        }

        if (widgetsToShow.length === 0) return null;

        return (
            <Stack
                direction="row"
                spacing={1}
                sx={{
                    flexWrap: 'wrap',
                    gap: 0.5,
                    alignItems: 'center',
                    flexShrink: 0,
                }}
            >
                {widgetsToShow}
            </Stack>
        );
    }, [activeWidgets, widgets, isWidgetVisible, alert]);


    if (!sessionId && preChatState) {
        if (PreChatFormComponent) {
            return (
                <ChatArea hasContentAbove={hasContentAbove}>
                    <Box sx={{ p: 2, height: '100%', overflowY: 'auto' }}>
                        <PreChatFormComponent
                            teamName={preChatState.teamName}
                            config={preChatState.config}
                            initialContext={preChatState.initialContext}
                            onStart={(context) => {
                                initializeSession(null, {
                                    team_name: preChatState.teamName,
                                    context: context,
                                });
                            }}
                        />
                    </Box>
                </ChatArea>
            );
        }
        return (
            <ChatArea hasContentAbove={hasContentAbove}>
                <Box sx={{ p: 2 }}>
                    <Alert severity="warning">A pre-chat form is required but not configured for this application.</Alert>
                </Box>
            </ChatArea>
        );
    }

    return (
        <ChatArea hasContentAbove={hasContentAbove}>
            {error && (
                <GutterWrapper>
                    <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>{error}</Alert>
                </GutterWrapper>
            )}
            <GutterWrapper sx={{ pt: hasContentAbove ? 0.5 : 2 }}>
                <ChatHeader buttons={headerButtons} widgets={renderedWidgets} />
            </GutterWrapper>
            <MessagesList>
                {timelineItems.length === 0 && !isInitializing && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', opacity: 0.7 }}>
                        <Typography variant="h5" gutterBottom> Welcome </Typography>
                        <Typography variant="body1" textAlign="center"> Start a session to begin your learning journey. </Typography>
                    </Box>
                )}
                {timelineItems.length > 0 && (
                    <ChatMessageList
                        timelineItems={timelineItems}
                        showProcessUpdates={showProcessUpdates}
                    />
                )}
            </MessagesList>
            <GutterWrapper sx={{ pb: 2 }}>
                <ChatInput
                    sessionId={sessionId}
                    isSessionReady={isSessionReady}
                    isSending={isSending}
                    onSendMessage={handleSendMessage}
                    placeholder={getPlaceholderText()}
                    isMobile={isMobile}
                />
            </GutterWrapper>
            {renderDialogs()}
        </ChatArea>
    );
};

export default ChatContainer;